import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  TrendingUp, TrendingDown, Minus, Users, ChefHat, Shield, Building2,
  Sparkles, Wallet, Download, FileSpreadsheet, Loader2, Heart, Target,
  AlertTriangle, Receipt, Banknote, Coins, ShoppingCart, Zap, Flame,
  Package, Wrench, Megaphone, HandCoins, Truck, Sprout, Milk, Utensils,
  Brush, Bug, Briefcase, Wifi, Cpu, Landmark, PiggyBank, FileText,
  Percent, ArrowUpRight, ArrowDownRight, ClipboardCheck, ScrollText,
} from "lucide-react";
import { FILL } from "./owner-reports/OwnerUI";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Canonical formatting helpers (aligned with Center Accounts) ──
import { resolveCurrency, formatMoney } from "../utils/money";

// Multi-country helpers. `cur` accepts an ISO code string OR the full meta
// object from `useCurrency()` — both work.
const currencyCodeFor = (country, fallback = "INR") =>
  country === "Australia" ? "AUD" : (country === "India" ? "INR" : fallback);
const symbolFor = (cur) => resolveCurrency(cur).symbol;

const fmt = (v, cur = "INR") => {
  const meta = resolveCurrency(cur);
  return formatMoney(v, meta, { digits: 0 });
};
const fmtL = (v, cur = "INR") => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  const meta = resolveCurrency(cur);
  const neg = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  // Lakh/Crore compaction is India-only. Every other currency shows the full
  // number formatted with its locale.
  if (meta.code === "INR") {
    if (abs >= 10000000) return `${neg}${meta.symbol}${(abs / 10000000).toFixed(2)} Cr`;
    if (abs >= 100000) return `${neg}${meta.symbol}${(abs / 100000).toFixed(2)} L`;
    return `${neg}${meta.symbol}${Math.round(abs).toLocaleString(meta.locale)}`;
  }
  return `${neg}${meta.symbol}${abs.toLocaleString(meta.locale, { maximumFractionDigits: 2 })}`;
};
const pctOf = (num, den) => {
  if (!den || Number(den) === 0) return null;
  return (Number(num) / Number(den)) * 100;
};
const num = (v) => Number(v || 0);

// ── Pale premium palette (cycles) ────────────────────────────
const PALE = [
  { bg: "bg-emerald-50/70", border: "border-emerald-200", icon: "text-emerald-700", label: "text-emerald-900" },
  { bg: "bg-sky-50/70",     border: "border-sky-200",     icon: "text-sky-700",     label: "text-sky-900" },
  { bg: "bg-amber-50/70",   border: "border-amber-200",   icon: "text-amber-700",   label: "text-amber-900" },
  { bg: "bg-rose-50/70",    border: "border-rose-200",    icon: "text-rose-700",    label: "text-rose-900" },
  { bg: "bg-purple-50/70",  border: "border-purple-200",  icon: "text-purple-700",  label: "text-purple-900" },
  { bg: "bg-teal-50/70",    border: "border-teal-200",    icon: "text-teal-700",    label: "text-teal-900" },
  { bg: "bg-orange-50/70",  border: "border-orange-200",  icon: "text-orange-700",  label: "text-orange-900" },
  { bg: "bg-lime-50/70",    border: "border-lime-200",    icon: "text-lime-700",    label: "text-lime-900" },
  { bg: "bg-cyan-50/70",    border: "border-cyan-200",    icon: "text-cyan-700",    label: "text-cyan-900" },
];
const paleFor = (str) => {
  const s = String(str || "").toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h) + s.charCodeAt(i);
  return PALE[Math.abs(h) % PALE.length];
};

// ── Expense-head icon mapping ────────────────────────────────
const HEAD_ICONS = [
  [/salary|payroll|wage|staff/i, Users],
  [/rent|lease/i, Building2],
  [/grocer|kirana|store/i, ShoppingCart],
  [/fruit|veg|produce/i, Sprout],
  [/dairy|milk|paneer|curd/i, Milk],
  [/electr|power|light/i, Zap],
  [/gas|cylinder|fuel/i, Flame],
  [/packag|container|box/i, Package],
  [/repair|maintain/i, Wrench],
  [/market|advert|promot/i, Megaphone],
  [/share|payout|profit|revenue/i, HandCoins],
  [/emi|loan|advance|instal/i, Landmark],
  [/gst|tax/i, Receipt],
  [/mango|rass|pav|celebr/i, Utensils],
  [/clean|housekeep/i, Brush],
  [/pest/i, Bug],
  [/transport|freight|delivery|logistic/i, Truck],
  [/profession|consult|legal|audit|fee|bank/i, Briefcase],
  [/internet|wifi/i, Wifi],
  [/software|subscription/i, Cpu],
  [/inter.?cent|water/i, PiggyBank],
];
const iconForHead = (head) => {
  for (const [re, Ic] of HEAD_ICONS) if (re.test(head || "")) return Ic;
  return Coins;
};

// ── Delta helpers ────────────────────────────────────────────
const computeDelta = (curr, prev) => {
  const c = num(curr), p = num(prev);
  if (p === 0 && c === 0) return { pct: 0, kind: "flat" };
  if (p === 0 && c > 0)  return { pct: null, kind: "new" };
  const d = ((c - p) / Math.abs(p)) * 100;
  return { pct: d, kind: d > 3 ? "up" : d < -3 ? "down" : "flat" };
};
const meaningForExpense = (d) => {
  if (d.kind === "new") return "New this month";
  if (d.pct === null) return "";
  if (d.pct > 10) return "Higher than last month";
  if (d.pct > 3)  return "Slight increase";
  if (d.pct < -10) return "Improved (large drop)";
  if (d.pct < -3)  return "Improving";
  return "Stable";
};
const meaningForSales = (d) => {
  if (d.pct === null) return "";
  if (d.pct > 10) return "Strong growth";
  if (d.pct > 3)  return "Positive movement";
  if (d.pct < -10) return "Sharp decline";
  if (d.pct < -3)  return "Softening";
  return "Stable";
};

// ── Delta badge ──────────────────────────────────────────────
function DeltaBadge({ d, invert = false }) {
  if (!d || d.pct === null) {
    if (d?.kind === "new") return <span className="text-[11px] font-medium text-slate-500">New</span>;
    return null;
  }
  const isGood = invert ? d.pct < 0 : d.pct > 0;
  const flat = Math.abs(d.pct) < 0.5;
  const color = flat ? "text-slate-500" : isGood ? "text-emerald-700" : "text-rose-700";
  const Icon = flat ? Minus : d.pct > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`text-[11px] font-semibold inline-flex items-center gap-0.5 ${color}`}>
      <Icon className="w-3 h-3" />
      {Math.abs(d.pct).toFixed(1)}% vs last month
    </span>
  );
}

// ── ClassicTile ──────────────────────────────────────────────
const SCV_FILLS = [FILL.emerald, FILL.blue, FILL.gold, FILL.purple, FILL.teal, FILL.green];
function ClassicTile({ icon: Icon, title, value, lines = [], paleKey, testid, footer, loss = false }) {
  const k = String(paleKey || title); let h = 0;
  for (let i = 0; i < k.length; i++) h = ((h << 5) - h) + k.charCodeAt(i);
  const fill = loss ? FILL.ruby : SCV_FILLS[Math.abs(h) % SCV_FILLS.length];
  return (
    <div data-testid={testid} className="relative rounded-2xl border-[3px] border-[#d9ae52] p-3 sm:p-4 text-white shadow-md" style={{ background: fill }}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="w-7 h-7 text-[#F4D58D] shrink-0" />}
        <p className="text-sm font-bold leading-tight">{title}</p>
      </div>
      <p className="text-2xl font-bold leading-tight break-words" style={{ fontFamily: "'Playfair Display', serif" }}>{value}</p>
      {loss && <span className="mt-1 inline-block rounded-full bg-black/25 px-2 py-0.5 text-xs font-bold uppercase">Attention</span>}
      <div className="mt-1 space-y-0.5">
        {lines.map((l, i) => (
          <p key={i} className="text-xs sm:text-sm opacity-95 leading-snug">{l}</p>
        ))}
      </div>
      {footer && <div className="mt-1.5 inline-block rounded-lg bg-white/90 px-2 py-1">{footer}</div>}
    </div>
  );
}

// ── Section header with optional single "View Report" button ──
function SectionHeader({ title, description, actionLabel, onAction, testid }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-2">
      <div>
        <h3 className="text-xl font-bold text-[#750817]" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h3>
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
      {actionLabel && onAction && (
        <Button size="sm" variant="outline" onClick={onAction} data-testid={testid}
                className="border-[#8B0000] text-[#8B0000] hover:bg-[#8B0000]/5">
          <FileText className="w-3.5 h-3.5 mr-1" /> {actionLabel}
        </Button>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Smart Center View — canonical read-only monthly snapshot
// Data sources (SINGLE SOURCE OF TRUTH):
//   • /api/center-accounts/summary (current + prev month)
//   • /api/sales/reports/monthly-summary (guests / bills / APC)
//   • /api/center-accounts/payout-summary (paid / pending)
// No independent calculations. All numbers match Center Accounts.
// ══════════════════════════════════════════════════════════════
export default function SmartCenterView({ session, center, month, currency: currencyProp, onOpenMonthly }) {
  const [loading, setLoading] = useState(false);
  const [caCurr, setCaCurr] = useState(null);       // center-accounts/summary → this month
  const [caPrev, setCaPrev] = useState(null);       // center-accounts/summary → prev month
  const [salesCurr, setSalesCurr] = useState(null); // sales/reports/monthly-summary → this month
  const [payoutData, setPayoutData] = useState(null); // center-accounts/payout-summary
  const [downloading, setDownloading] = useState(null);
  const captureRef = useRef(null);

  const goToMonthly = useCallback((section) => {
    if (typeof onOpenMonthly === "function") onOpenMonthly(section);
  }, [onOpenMonthly]);

  const prevMonth = useMemo(() => {
    if (!month) return null;
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 2, 1); // m is 1-based → subtract 1 for JS then 1 more for prev
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, [month]);

  const loadAll = useCallback(async () => {
    if (!center || !month || !session?.token) return;
    setLoading(true);
    try {
      const headers = { "Content-Type": "application/json" };
      const [caCurrRes, caPrevRes, salesRes, payoutRes] = await Promise.all([
        fetch(`${API}/api/center-accounts/summary`, {
          method: "POST", headers,
          body: JSON.stringify({ token: session.token, center, month }),
        }),
        fetch(`${API}/api/center-accounts/summary`, {
          method: "POST", headers,
          body: JSON.stringify({ token: session.token, center, month: prevMonth }),
        }),
        fetch(`${API}/api/sales/reports/monthly-summary`, {
          method: "POST", headers,
          body: JSON.stringify({ token: session.token, center, month }),
        }),
        fetch(`${API}/api/center-accounts/payout-summary`, {
          method: "POST", headers,
          body: JSON.stringify({ token: session.token, center, month }),
        }),
      ]);
      const caC = caCurrRes.ok ? (await caCurrRes.json())?.summary : null;
      const caP = caPrevRes.ok ? (await caPrevRes.json())?.summary : null;
      const salesJ = salesRes.ok ? await salesRes.json() : null;
      const sales = salesJ?.summary || salesJ?.grand_total || null;
      const payout = payoutRes.ok ? await payoutRes.json() : null;
      setCaCurr(caC); setCaPrev(caP); setSalesCurr(sales); setPayoutData(payout);
    } catch (e) {
      toast.error("Failed to load smart view");
    } finally {
      setLoading(false);
    }
  }, [session, center, month, prevMonth]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Derive canonical intelligence ────────────────────────────
  const smart = useMemo(() => {
    if (!caCurr) return null;
    const country = caCurr.country || "India";
    const currency = currencyProp || currencyCodeFor(country, "INR");

    // ── Core figures from Center Accounts ──
    const sales = num(caCurr.sales?.total_sale);
    const expenses = num(caCurr.expenses?.total);
    const commissions = num(caCurr.commissions?.total_with_gst ?? caCurr.commissions?.total);
    const gstPaid = num(caCurr.financial_summary?.sales_gst);

    // ── P/L: use operational_sustainability.profit_loss (Center Accounts canonical) ──
    const os = caCurr.operational_sustainability || {};
    const profitLoss = num(os.profit_loss ?? os.operational_balance ?? (sales - expenses - commissions));
    const isProfit = profitLoss >= 0;

    // ── Australia "Profitability" (financial_summary.profitability — Base for 80/20 Split) ──
    const profitability = num(caCurr.financial_summary?.profitability);
    const isProfitabilityLoss = profitability < 0;

    // ── Working Capital (from working_capital_status — canonical) ──
    const wcs = caCurr.working_capital_status || {};
    const wcBase = num(wcs.base_wc);
    const wcCurrent = num(wcs.current_wc);
    const wcPct = num(wcs.wc_percentage);
    const wcStatus = wcs.status || (wcs.protection_mode ? "Protection Mode" : "Healthy");
    const protectionMode = Boolean(wcs.protection_mode);
    const wcUsed = num(wcs.cumulative_wc_used);
    const wcGap = Math.max(0, wcBase - wcCurrent);

    // ── Sales Dashboard values ──
    const totalGuests = num(salesCurr?.total_guests);
    const totalBills = num(salesCurr?.total_bills);
    const avgPerCover = num(salesCurr?.avg_per_pax) || (totalGuests > 0 ? sales / totalGuests : null);
    const cashSale = num(caCurr.sales?.total_cash_sale ?? salesCurr?.total_cash_sale);
    const onlineSale = num(salesCurr?.total_online_sale);
    const cardSale = num(caCurr.sales?.card_sale ?? salesCurr?.total_card_idfc);
    const deliverySale = num(caCurr.sales?.aggregator_sale);
    const daysRecorded = num(caCurr.sales?.num_days ?? salesCurr?.days_count);

    // ── Payout model (Revenue Share vs Profit Share) ──
    const payoutModel = caCurr.payout_model || "revenue_share";
    const isProfitShare = payoutModel === "profit_share";
    const baseLabel = caCurr.base_label || (isProfitShare ? "Profit Share Base" : "Revenue Share Base");
    const engine = caCurr.engine || {};
    const rsBase = num(engine.revenue_share_base ?? caCurr.share_calculation?.net_profit_or_sales);
    const psBase = num(engine.profit_share_base ?? profitLoss);
    const selectedBase = num(engine.selected_base ?? (isProfitShare ? psBase : rsBase));

    const share = caCurr.share_calculation || {};
    const ownerSharePct = num(engine.owner_pct ?? share.franchise_owner?.percentage);
    const ownerShareAmt = num(engine.owner_share ?? share.franchise_owner?.amount);
    const companyShareAmt = num(engine.company_share ?? share.purnabramha?.total_payable);
    const shareGstAmount = num(share.purnabramha?.gst_amount);
    // Share GST rate is canonical from tax_rules (18% for India, 10% for AUS/outside)
    const shareGstRate = num(caCurr.tax_rules?.share_gst_rate);
    // Revenue Share + GST = owner share × (1 + share_gst_rate/100).
    // This is the owner's invoice-to-Purnabramha total (owner pays GST on their service).
    const revenueShareWithGst = ownerShareAmt * (1 + shareGstRate / 100);

    // ── MG ──
    const mgApplicable = Boolean(caCurr.mg_calculation_applicable);
    const monthlyMG = num(caCurr.mg_calculation?.monthly_mg);
    // MG + GST uses the same canonical share GST rate
    const mgWithGst = monthlyMG * (1 + shareGstRate / 100);

    // ── Amount Paid / Pending — from payout-summary monthly_data[month] ──
    const monthRow = (payoutData?.monthly_data || []).find((r) => r.month === month);
    const amountPaid = num(monthRow?.paid);
    const amountPayable = num(monthRow?.payable_amount);
    const amountPending = num(monthRow?.pending ?? Math.max(0, amountPayable - amountPaid));
    const payableType = monthRow?.payable_type || (isProfitShare ? "profit_share" : "revenue_share");
    const payoutStatus = monthRow?.status || "unpaid";

    // ── Payout Release Status (from summary) ──
    const releaseStatus = caCurr.payout_release_status || {};

    // ── Adjustments ──
    const adjustments = num(caCurr.expense_adjustments?.total_adjustments);

    // ── Sales & expenses deltas ──
    const prevSales = num(caPrev?.sales?.total_sale);
    const prevExpenses = num(caPrev?.expenses?.total);
    const salesDelta = computeDelta(sales, prevSales);
    const expDelta = computeDelta(expenses, prevExpenses);

    // ── Expense heads — merge current + previous for delta comparison ──
    const currentHeads = caCurr.expenses?.by_category || {};
    const previousHeads = caPrev?.expenses?.by_category || {};
    const allKeys = new Set([...Object.keys(currentHeads), ...Object.keys(previousHeads)]);
    const heads = Array.from(allKeys)
      .map((k) => {
        const cur = num(currentHeads[k]);
        const prev = num(previousHeads[k]);
        if (cur === 0) return null; // skip zero-in-current
        const d = computeDelta(cur, prev);
        return {
          head: k,
          amount: cur,
          previous: prev,
          pctOfExpenses: pctOf(cur, expenses),
          pctOfSales: pctOf(cur, sales),
          delta: d,
          meaning: meaningForExpense(d),
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.amount - a.amount);

    // ── Operational discipline ──
    const daysInMonth = 30; // approximation for percent display
    const attendancePct = daysRecorded > 0 ? Math.min(100, (daysRecorded / daysInMonth) * 100) : 0;
    const kitchenPctOfSales = pctOf(expenses, sales) ?? 0;
    const kitchenSafeBand = { low: 25, high: 45 };
    const withinBand = kitchenPctOfSales >= kitchenSafeBand.low && kitchenPctOfSales <= kitchenSafeBand.high;

    // ── Positive story (data-driven; references canonical numbers) ──
    let positive = "";
    const emptyMonth = sales === 0 && expenses === 0;
    if (emptyMonth) {
      positive = "No sales or expenses recorded yet for this month — insights will populate as data comes in.";
    } else {
      const parts = [];
      if (isProfit && profitLoss > 0) {
        parts.push(`Center closed the month with a net ${isProfitShare ? "profit" : "operational surplus"} of ${fmt(profitLoss, currency)}.`);
      } else if (profitLoss < 0) {
        parts.push(`Center closed with an operational shortfall of ${fmt(Math.abs(profitLoss), currency)} — attention needed.`);
      }
      if (wcCurrent > 0 && wcCurrent >= wcBase) {
        parts.push(`Working capital is healthy at ${fmt(wcCurrent, currency)} (${wcPct.toFixed(0)}% of base).`);
      } else if (wcGap > 0) {
        parts.push(`Working capital sits at ${fmt(wcCurrent, currency)} against a base of ${fmt(wcBase, currency)} — a gap of ${fmt(wcGap, currency)}.`);
      }
      if (salesDelta.kind === "up") parts.push(`Sales grew ${Math.abs(salesDelta.pct).toFixed(1)}% vs last month.`);
      else if (salesDelta.kind === "down") parts.push(`Sales dropped ${Math.abs(salesDelta.pct).toFixed(1)}% vs last month.`);
      if (expDelta.kind === "down") parts.push(`Expenses reduced ${Math.abs(expDelta.pct).toFixed(1)}%.`);
      else if (expDelta.kind === "up") parts.push(`Expenses rose ${Math.abs(expDelta.pct).toFixed(1)}%.`);
      if (parts.length === 0) parts.push("Steady month across the board.");
      positive = parts.join(" ");
    }

    // ── Improved / Attention / Priorities ──
    const improved = [];
    const attention = [];
    if (isProfit) improved.push(`Net ${isProfitShare ? "profit" : "surplus"} ${fmt(profitLoss, currency)}`);
    if (salesDelta.kind === "up") improved.push(`Sales up ${Math.abs(salesDelta.pct).toFixed(1)}%`);
    if (expDelta.kind === "down") improved.push(`Expenses down ${Math.abs(expDelta.pct).toFixed(1)}%`);
    if (wcCurrent >= wcBase && wcBase > 0) improved.push(`Working capital healthy (${wcPct.toFixed(0)}%)`);
    if (daysRecorded >= 25) improved.push(`Reporting discipline ${daysRecorded}/~30 days`);

    if (!isProfit) attention.push(`Operational loss ${fmt(Math.abs(profitLoss), currency)}`);
    if (salesDelta.kind === "down") attention.push(`Sales down ${Math.abs(salesDelta.pct).toFixed(1)}%`);
    if (expDelta.kind === "up") attention.push(`Expenses up ${Math.abs(expDelta.pct).toFixed(1)}%`);
    if (wcGap > 0) attention.push(`Working capital gap ${fmt(wcGap, currency)}`);
    if (protectionMode) attention.push("Protection Mode ACTIVE — payout may be gated");
    if (daysRecorded > 0 && daysRecorded < 20 && !emptyMonth) attention.push(`Only ${daysRecorded} days recorded — reporting gap`);
    if (amountPending > 0) attention.push(`${fmt(amountPending, currency)} payout pending`);

    if (improved.length === 0) improved.push("Data tracking discipline maintained");
    if (attention.length === 0) attention.push("No urgent attention areas");

    const priorities = [];
    if (wcGap > 0) priorities.push(`Rebuild working capital by ~${fmt(wcGap, currency)}`);
    if (!isProfit) priorities.push("Restore operational profitability");
    if (amountPending > 0) priorities.push(`Follow up on ${fmt(amountPending, currency)} pending payout`);
    if (expDelta.kind === "up") priorities.push("Review top expense heads for optimisation");
    if (priorities.length === 0) priorities.push("Continue current discipline — no urgent focus areas");

    // ── Owner Message ──
    let ownerMessage;
    if (emptyMonth) {
      ownerMessage = "Once daily sales and expenses start recording, we'll be able to tell you exactly where to protect cash.";
    } else {
      const bits = [];
      bits.push(`This month closed at ${fmt(sales, currency)} in sales and ${fmt(expenses, currency)} in expenses`);
      if (isProfit) bits.push(`with a positive result of ${fmt(profitLoss, currency)}.`);
      else bits.push(`with an operational shortfall of ${fmt(Math.abs(profitLoss), currency)}.`);
      if (protectionMode) bits.push("Working capital is in Protection Mode — payout is gated until the WC threshold recovers.");
      else if (wcGap > 0) bits.push(`Working capital shows a gap of ${fmt(wcGap, currency)} — plan replenishment soon.`);
      ownerMessage = bits.join(" ");
    }

    return {
      // core
      country, currency, sales, expenses, commissions, gstPaid, profitLoss, isProfit,
      profitability, isProfitabilityLoss,
      // sales dashboard
      totalGuests, totalBills, avgPerCover, cashSale, onlineSale, cardSale, deliverySale, daysRecorded,
      // working capital
      wcBase, wcCurrent, wcPct, wcStatus, protectionMode, wcUsed, wcGap,
      // payout / model
      payoutModel, isProfitShare, baseLabel, rsBase, psBase, selectedBase,
      ownerSharePct, ownerShareAmt, companyShareAmt, revenueShareWithGst, shareGstAmount, shareGstRate,
      mgApplicable, monthlyMG, mgWithGst,
      amountPaid, amountPayable, amountPending, payableType, payoutStatus, releaseStatus,
      // deltas
      salesDelta, expDelta,
      // adjustments
      adjustments,
      // sections
      heads, attendancePct, kitchenPctOfSales, kitchenSafeBand, withinBand, daysInMonth,
      positive, improved, attention, priorities, ownerMessage, emptyMonth,
    };
  }, [caCurr, caPrev, salesCurr, payoutData, currencyProp, month]);

  // ── PDF / Excel downloads ────────────────────────────────────
  const downloadSmartPDF = async () => {
    setDownloading("pdf");
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");
      const el = captureRef.current;
      if (!el) throw new Error("Nothing to capture");
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: "#fdf6ec", useCORS: true, logging: false });
      const imgData = canvas.toDataURL("image/jpeg", 0.9);
      const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [canvas.width, canvas.height] });
      pdf.addImage(imgData, "JPEG", 0, 0, canvas.width, canvas.height);
      pdf.save(`SmartCenterView_${center}_${month}.pdf`);
      toast.success("Smart PDF downloaded");
    } catch (e) { toast.error(e.message || "Failed to generate PDF"); }
    finally { setDownloading(null); }
  };

  const downloadSmartExcel = async () => {
    if (!smart) return;
    setDownloading("excel");
    try {
      const XLSX = await import("xlsx");
      const wb = XLSX.utils.book_new();
      const summary = [
        ["Purnabramha — Smart Center View (Canonical)", "", "", ""],
        ["Centre", center, "Month", month],
        ["Country", smart.country, "Payout Model", smart.payoutModel],
        ["", "", "", ""],
        ["Total Sales", smart.sales, "Δ vs last month %", smart.salesDelta.pct],
        ["Total Expenses", smart.expenses, "Δ vs last month %", smart.expDelta.pct],
        ["Commissions (incl GST)", smart.commissions, "GST on Sales", smart.gstPaid],
        ["Adjustments", smart.adjustments, "", ""],
        ["Net Profit / Loss (Op Balance)", smart.profitLoss, "Profitability", smart.profitability],
        ["Base Label", smart.baseLabel, "Selected Base", smart.selectedBase],
        ["Revenue Share Base", smart.rsBase, "Profit Share Base", smart.psBase],
        ["Owner Share %", smart.ownerSharePct, "Owner Share Amt", smart.ownerShareAmt],
        ["Company Share", smart.companyShareAmt, "GST on Share", smart.shareGstAmount],
        ["MG", smart.monthlyMG, "MG + GST", smart.mgWithGst],
        ["Amount Paid", smart.amountPaid, "Pending", smart.amountPending],
        ["Working Capital Base", smart.wcBase, "Current WC", smart.wcCurrent],
        ["WC %", smart.wcPct, "Protection Mode", smart.protectionMode ? "YES" : "NO"],
        ["Total Guests", smart.totalGuests, "Total Bills", smart.totalBills],
        ["APC", smart.avgPerCover, "", ""],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Summary");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(smart.heads.map((h) => ({
        head: h.head,
        amount: h.amount,
        previous_month: h.previous,
        pct_of_expenses: h.pctOfExpenses,
        pct_of_sales: h.pctOfSales,
        delta_pct_vs_last_month: h.delta.pct,
        meaning: h.meaning,
      }))), "Expense Detail");
      XLSX.writeFile(wb, `SmartCenterView_${center}_${month}.xlsx`);
      toast.success("Smart Excel downloaded");
    } catch (e) { toast.error(e.message || "Failed to generate Excel"); }
    finally { setDownloading(null); }
  };

  // ── Empty states ────────────────────────────────────────────
  if (!center) {
    return (
      <Card className="border-slate-200/60">
        <CardContent className="p-6 text-center text-slate-500">
          Select a centre to view the Smart Center View.
        </CardContent>
      </Card>
    );
  }
  if (loading && !caCurr) {
    return (
      <Card className="border-slate-200/60">
        <CardContent className="p-6 text-center text-slate-500 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading canonical view…
        </CardContent>
      </Card>
    );
  }
  if (!smart) return null;

  const currency = smart.currency;
  const country = smart.country;

  return (
    <div className="space-y-4" data-testid="smart-center-view">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#8B0000]" /> Smart Center View
            <Badge variant="outline" className="text-[10px] ml-2" data-testid="smart-canonical-badge">
              Canonical · matches Center Accounts
            </Badge>
          </h2>
          <p className="text-xs text-slate-500">
            Read-only monthly snapshot · {center} · {month} · {country} · {smart.isProfitShare ? "Profit Share" : "Revenue Share"} model
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={downloadSmartPDF} disabled={!!downloading} data-testid="smart-download-pdf-btn">
            {downloading === "pdf" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
            Smart PDF
          </Button>
          <Button size="sm" variant="outline" onClick={downloadSmartExcel} disabled={!!downloading} data-testid="smart-download-excel-btn">
            {downloading === "excel" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}
            Smart Excel
          </Button>
        </div>
      </div>

      {/* Snapshot capture area — cream Purnabramha background */}
      <div ref={captureRef} className="space-y-4 p-4 rounded-xl" style={{ backgroundColor: "#fdf6ec" }}>
        <div className="flex items-center justify-between border-b-2 border-[#8B0000]/20 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#8B0000] text-white flex items-center justify-center font-bold text-lg shadow">P</div>
            <div>
              <p className="text-lg font-bold text-[#8B0000]">Purnabramha</p>
              <p className="text-xs text-slate-600">{center} · {month} · Business Health Card</p>
            </div>
          </div>
          <p className="text-xs text-slate-500">Generated {new Date().toLocaleDateString(currency === "AUD" ? "en-AU" : "en-IN")}</p>
        </div>

        {/* ── B. BUSINESS NUMBERS (canonical) ────────────────── */}
        <div>
          <SectionHeader title="Business Numbers" description="Same figures as Center Accounts + Sales Dashboard" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ClassicTile
              icon={Banknote} title="Total Sales" paleKey="sales-tile"
              value={fmtL(smart.sales, currency)} testid="smart-tile-sales"
              lines={[smart.daysRecorded > 0 ? `${smart.daysRecorded} day(s) recorded` : "No days recorded yet"]}
              footer={<div className="flex items-center gap-2"><DeltaBadge d={smart.salesDelta} /><span className="text-[10px] text-slate-500 italic">{meaningForSales(smart.salesDelta)}</span></div>}
            />
            <ClassicTile
              icon={Receipt} title="Total Expenses" paleKey="expenses-tile"
              value={fmtL(smart.expenses, currency)} testid="smart-tile-expenses"
              lines={[
                smart.sales > 0 ? `${(pctOf(smart.expenses, smart.sales) ?? 0).toFixed(1)}% of sales` : "—",
                smart.adjustments !== 0 ? `Adjustments: ${fmtL(smart.adjustments, currency)}` : null,
              ].filter(Boolean)}
              footer={<DeltaBadge d={smart.expDelta} invert />}
            />
            <ClassicTile
              icon={smart.isProfit ? TrendingUp : TrendingDown}
              title={smart.isProfit ? "Net Profit / Operational Balance" : "Net Loss"}
              paleKey="pl-tile" loss={!smart.isProfit}
              value={fmtL(smart.profitLoss, currency)} testid="smart-tile-pl"
              lines={[
                smart.isProfit ? "Sales − Expenses − Commissions" : "Loss — attention needed",
                country === "Australia" ? `Profitability: ${fmtL(smart.profitability, currency)}` : `${smart.baseLabel}: ${fmtL(smart.selectedBase, currency)}`,
              ]}
            />
            <ClassicTile
              icon={Wallet} title="Working Capital" paleKey="wc-tile"
              loss={smart.protectionMode || smart.wcCurrent < 0}
              value={fmtL(smart.wcCurrent, currency)} testid="smart-tile-wc"
              lines={[
                `Base: ${fmtL(smart.wcBase, currency)} · ${smart.wcPct.toFixed(0)}%`,
                smart.protectionMode ? "Protection Mode ACTIVE" : `Status: ${smart.wcStatus}`,
                smart.wcGap > 0 ? `Gap: ${fmtL(smart.wcGap, currency)}` : null,
              ].filter(Boolean)}
            />

            {/* Amount Paid + Pending */}
            <ClassicTile
              icon={HandCoins} title="Amount Paid (Owner Payout)" paleKey="paid-tile"
              value={fmtL(smart.amountPaid, currency)} testid="smart-tile-paid"
              lines={[
                `Payable: ${fmtL(smart.amountPayable, currency)}`,
                `Status: ${smart.payoutStatus}`,
                smart.payableType ? `Type: ${smart.payableType}` : null,
              ].filter(Boolean)}
            />
            <ClassicTile
              icon={AlertTriangle} title="Pending Payout" paleKey="pending-tile"
              loss={smart.amountPending > 0}
              value={fmtL(smart.amountPending, currency)} testid="smart-tile-pending"
              lines={[
                smart.amountPending > 0 ? "Awaiting release" : "Nothing pending",
                smart.releaseStatus?.label ? `Release: ${smart.releaseStatus.label}` : null,
              ].filter(Boolean)}
            />

            {/* Sales Dashboard values — Guests + APC */}
            <ClassicTile
              icon={Users} title="Total Guests" paleKey="guests-tile"
              value={smart.totalGuests > 0 ? smart.totalGuests.toLocaleString(currency === "AUD" ? "en-AU" : "en-IN") : (smart.totalGuests === 0 && smart.emptyMonth ? "—" : "0")}
              testid="smart-tile-guests"
              lines={[
                smart.totalBills > 0 ? `${smart.totalBills.toLocaleString(currency === "AUD" ? "en-AU" : "en-IN")} bills` : "0 bills",
                smart.totalGuests > 0 && smart.totalBills > 0 ? `${(smart.totalGuests / smart.totalBills).toFixed(1)} guests/bill` : null,
              ].filter(Boolean)}
            />
            <ClassicTile
              icon={Utensils} title="Average Per Cover (APC)" paleKey="apc-tile"
              value={smart.avgPerCover ? fmt(smart.avgPerCover, currency) : "—"}
              testid="smart-tile-apc"
              lines={[smart.avgPerCover ? "Average bill per guest" : "APC unavailable"]}
            />
          </div>
        </div>

        {/* ── C. Positive story ─────────────────────────────── */}
        <div className="rounded-lg border-2 border-amber-200 bg-gradient-to-br from-yellow-50 to-amber-50 p-4" data-testid="smart-positive-story">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-amber-700" />
            <h3 className="text-sm font-bold text-amber-900 uppercase tracking-wider">This Month&apos;s Positive Story</h3>
          </div>
          <p className="text-sm text-slate-800 leading-relaxed">{smart.positive}</p>
        </div>

        {/* ── D. Financial Foundation ───────────────────────── */}
        <div>
          <SectionHeader
            title="Financial Foundation"
            description="Working capital + payout base + adjustments (from Center Accounts)"
            actionLabel="View Owner Ledger"
            onAction={() => goToMonthly("owner-ledger")}
            testid="smart-view-owner-ledger-btn"
          />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ClassicTile
              icon={Wallet} title="Base Working Capital" paleKey="ff-basewc"
              value={fmtL(smart.wcBase, currency)}
              lines={[`Initial deposit / cushion`]}
            />
            <ClassicTile
              icon={Wallet} title="Current Working Capital" paleKey="ff-currwc"
              loss={smart.protectionMode}
              value={fmtL(smart.wcCurrent, currency)}
              lines={[`${smart.wcPct.toFixed(0)}% of base`, smart.protectionMode ? "Protection Mode ACTIVE" : `Status: ${smart.wcStatus}`]}
            />
            <ClassicTile
              icon={ScrollText} title={smart.baseLabel} paleKey="ff-base"
              value={fmtL(smart.selectedBase, currency)}
              lines={[`${smart.isProfitShare ? "PBT-based payout" : "Sales − Comm − GST"}`, `Owner: ${smart.ownerSharePct}%`]}
            />
            <ClassicTile
              icon={AlertTriangle} title="Cash Gap" paleKey="ff-gap"
              loss={smart.wcGap > 0}
              value={fmtL(smart.wcGap, currency)}
              lines={[smart.wcGap > 0 ? "Needs replenishment" : "No immediate pressure"]}
            />
          </div>
        </div>

        {/* ── E. Payout details (model-aware) ─────────────────── */}
        <div>
          <SectionHeader
            title={smart.isProfitShare ? "Profit Share Details" : "Revenue Share Details"}
            description={smart.isProfitShare ? "Franchise + MFPL share, MG, amount paid, pending" : "Franchise share, MG, amount paid, pending"}
            actionLabel={smart.isProfitShare ? "View Profit Share Detail" : "View Revenue Share Detail"}
            onAction={() => goToMonthly("owner-ledger")}
            testid="smart-view-share-detail-btn"
          />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {!smart.isProfitShare && (
              <>
                <ClassicTile
                  icon={HandCoins} title="Revenue Share" paleKey="rs-owner"
                  value={fmtL(smart.ownerShareAmt, currency)}
                  lines={[`${smart.ownerSharePct}% of ${smart.baseLabel}`, `Base: ${fmtL(smart.rsBase, currency)}`]}
                  testid="smart-tile-revenue-share"
                />
                <ClassicTile
                  icon={Percent} title="Revenue Share + GST" paleKey="rs-gst"
                  value={fmtL(smart.revenueShareWithGst, currency)}
                  lines={[`GST on share: ${smart.shareGstRate || 0}%`, smart.shareGstAmount ? `Reference GST amt: ${fmtL(smart.shareGstAmount, currency)}` : null].filter(Boolean)}
                  testid="smart-tile-rs-gst"
                />
              </>
            )}
            {smart.isProfitShare && (
              <>
                <ClassicTile
                  icon={HandCoins} title="Franchise Share" paleKey="ps-owner"
                  value={fmtL(smart.ownerShareAmt, currency)}
                  lines={[`${smart.ownerSharePct}% of ${smart.baseLabel}`, `Base: ${fmtL(smart.psBase, currency)}`]}
                  testid="smart-tile-franchise-share"
                />
                <ClassicTile
                  icon={Percent} title="MFPL Share" paleKey="ps-mfpl"
                  value={fmtL(smart.companyShareAmt, currency)}
                  lines={[`${(100 - smart.ownerSharePct).toFixed(0)}% of ${smart.baseLabel}`]}
                  testid="smart-tile-mfpl-share"
                />
              </>
            )}
            {smart.mgApplicable && !smart.isProfitShare && (
              <>
                <ClassicTile
                  icon={Landmark} title="MG (Monthly)" paleKey="rs-mg"
                  value={fmtL(smart.monthlyMG, currency)}
                  lines={[`Minimum guarantee`]}
                  testid="smart-tile-mg"
                />
                <ClassicTile
                  icon={Percent} title="MG + GST" paleKey="rs-mg-gst"
                  value={fmtL(smart.mgWithGst, currency)}
                  lines={[`With ${smart.shareGstRate || 0}% GST`]}
                  testid="smart-tile-mg-gst"
                />
              </>
            )}
            <ClassicTile
              icon={HandCoins} title="Amount Paid" paleKey="pay-paid"
              value={fmtL(smart.amountPaid, currency)}
              lines={[`Of ${fmtL(smart.amountPayable, currency)} payable`, `Status: ${smart.payoutStatus}`]}
              testid="smart-tile-amount-paid"
            />
            <ClassicTile
              icon={AlertTriangle} title="Pending" paleKey="pay-pending"
              loss={smart.amountPending > 0}
              value={fmtL(smart.amountPending, currency)}
              lines={[
                smart.releaseStatus?.label ? `Release: ${smart.releaseStatus.label}` : "",
                smart.protectionMode ? "Gated by Protection Mode" : null,
              ].filter(Boolean)}
              testid="smart-tile-pending-detail"
            />
          </div>
        </div>

        {/* ── F. Operational Discipline ────────────────────── */}
        <div>
          <SectionHeader title="Operational Discipline" description="Attendance, kitchen cost, reporting — all read-only" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <ClassicTile
              icon={Users} title="Reporting Days" paleKey="op-att"
              value={`${smart.daysRecorded}/~30`}
              testid="smart-tile-reporting"
              lines={[`${smart.attendancePct.toFixed(0)}% of ~30 days`]}
            />
            <ClassicTile
              icon={ChefHat} title="Expenses / Sales" paleKey="op-kitchen"
              value={`${smart.kitchenPctOfSales.toFixed(1)}%`}
              testid="smart-tile-kitchen"
              lines={[
                `Safe band ${smart.kitchenSafeBand.low}-${smart.kitchenSafeBand.high}%`,
                smart.withinBand ? "Within band" : "Outside band — review",
              ]}
            />
            <ClassicTile
              icon={ClipboardCheck} title="Total Bills" paleKey="op-bills"
              value={smart.totalBills.toLocaleString(currency === "AUD" ? "en-AU" : "en-IN")}
              testid="smart-tile-bills"
              lines={[smart.totalGuests > 0 ? `${smart.totalGuests.toLocaleString(currency === "AUD" ? "en-AU" : "en-IN")} guests` : "0 guests"]}
            />
            <ClassicTile
              icon={Shield} title="Commissions Paid" paleKey="op-comm"
              value={fmtL(smart.commissions, currency)}
              testid="smart-tile-commissions"
              lines={[smart.sales > 0 ? `${(pctOf(smart.commissions, smart.sales) ?? 0).toFixed(1)}% of sales` : ""]}
            />
            <ClassicTile
              icon={Target} title="GST Paid" paleKey="op-gst"
              value={fmtL(smart.gstPaid, currency)}
              testid="smart-tile-gst"
              lines={[smart.sales > 0 ? `${(pctOf(smart.gstPaid, smart.sales) ?? 0).toFixed(1)}% of sales` : ""]}
            />
          </div>
        </div>

        {/* ── G. Expense Summary ────────────────────────────── */}
        <div>
          <SectionHeader
            title="Expense Summary — Where the Money Went"
            description={smart.heads.length > 0 ? `${smart.heads.length} active heads · sorted by amount · from Center Accounts` : "No expenses recorded"}
            actionLabel="View Expense Report"
            onAction={() => goToMonthly("expenses")}
            testid="smart-view-expense-report-btn"
          />
          {smart.heads.length === 0 ? (
            <div className="rounded-lg border-2 border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              No expense entries were recorded for this month.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="smart-expense-grid">
              {smart.heads.map((h) => {
                const Ic = iconForHead(h.head);
                const pal = paleFor(h.head);
                const meaningColor =
                  h.delta.kind === "up" ? "text-rose-700" :
                  h.delta.kind === "down" ? "text-emerald-700" : "text-slate-600";
                return (
                  <div
                    key={h.head}
                    className={`rounded-lg border-2 ${pal.border} ${pal.bg} p-3 shadow-sm`}
                    data-testid={`smart-expense-${(h.head || "").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase()}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Ic className={`w-4 h-4 ${pal.icon}`} />
                      <p className={`text-[11px] uppercase tracking-wider font-bold ${pal.label} truncate`} title={h.head}>
                        {h.head}
                      </p>
                    </div>
                    <p className="text-lg font-extrabold text-slate-900">{fmtL(h.amount, currency)}</p>
                    <p className="text-[11px] text-slate-700 mt-1">
                      <span className="font-semibold">{h.pctOfExpenses !== null ? h.pctOfExpenses.toFixed(1) + "%" : "—"}</span> of expenses
                    </p>
                    <p className="text-[11px] text-slate-700">
                      <span className="font-semibold">{h.pctOfSales !== null ? h.pctOfSales.toFixed(1) + "%" : "—"}</span> of sales
                    </p>
                    <div className="mt-1 flex items-center justify-between gap-1">
                      <DeltaBadge d={h.delta} invert />
                    </div>
                    <p className={`text-[10px] font-semibold italic ${meaningColor} mt-0.5`}>{h.meaning}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── H, I, J ────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/70 p-3" data-testid="smart-improved-card">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-emerald-700" />
              <h3 className="text-sm font-bold text-emerald-900 uppercase tracking-wider">What Improved</h3>
            </div>
            <ul className="text-sm text-slate-800 space-y-1 list-disc list-inside">
              {smart.improved.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
          <div className="rounded-lg border-2 border-amber-200 bg-amber-50/70 p-3" data-testid="smart-attention-card">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
              <h3 className="text-sm font-bold text-amber-900 uppercase tracking-wider">What Needs Attention</h3>
            </div>
            <ul className="text-sm text-slate-800 space-y-1 list-disc list-inside">
              {smart.attention.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
          <div className="rounded-lg border-2 border-sky-200 bg-sky-50/70 p-3" data-testid="smart-priorities-card">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-sky-700" />
              <h3 className="text-sm font-bold text-sky-900 uppercase tracking-wider">Next Month Priorities</h3>
            </div>
            <ul className="text-sm text-slate-800 space-y-1 list-disc list-inside">
              {smart.priorities.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
        </div>

        {/* ── Owner Message ─────────────────────────────────── */}
        <div className="rounded-lg border-2 border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50 p-4" data-testid="smart-owner-message">
          <div className="flex items-center gap-2 mb-2">
            <Heart className="w-4 h-4 text-rose-700" />
            <h3 className="text-sm font-bold text-rose-900 uppercase tracking-wider">Message to Franchise Owner</h3>
          </div>
          <p className="text-sm italic text-slate-800 leading-relaxed">&ldquo;{smart.ownerMessage}&rdquo;</p>
        </div>

        {/* ── Detailed Reports (only route to Tab 1) ─────────── */}
        <div className="rounded-lg border-2 border-slate-200 bg-white p-4" data-testid="smart-report-actions">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-slate-700" />
            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Detailed Reports</h3>
            <Badge variant="outline" className="text-[10px]">All open inside Owner Reports · Monthly Reports tab</Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            <Button size="sm" variant="outline" onClick={() => goToMonthly("sales")} data-testid="smart-view-sales-btn"
                    className="justify-start border-[#8B0000]/40 text-[#8B0000] hover:bg-[#8B0000]/5">
              <Banknote className="w-4 h-4 mr-1" /> Sales Report
            </Button>
            <Button size="sm" variant="outline" onClick={() => goToMonthly("expenses")} data-testid="smart-view-expenses-btn"
                    className="justify-start border-[#8B0000]/40 text-[#8B0000] hover:bg-[#8B0000]/5">
              <Receipt className="w-4 h-4 mr-1" /> Expense Report
            </Button>
            <Button size="sm" variant="outline" onClick={() => goToMonthly("commissions")} data-testid="smart-view-commissions-btn"
                    className="justify-start border-[#8B0000]/40 text-[#8B0000] hover:bg-[#8B0000]/5">
              <HandCoins className="w-4 h-4 mr-1" /> Commission Report
            </Button>
            <Button size="sm" variant="outline" onClick={() => goToMonthly("gst")} data-testid="smart-view-gst-btn"
                    className="justify-start border-[#8B0000]/40 text-[#8B0000] hover:bg-[#8B0000]/5">
              <Percent className="w-4 h-4 mr-1" /> GST Report
            </Button>
            <Button size="sm" variant="outline" onClick={() => goToMonthly("owner-ledger")} data-testid="smart-view-ledger-btn"
                    className="justify-start border-[#8B0000]/40 text-[#8B0000] hover:bg-[#8B0000]/5">
              <FileText className="w-4 h-4 mr-1" /> Owner Ledger
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
